// ترتیب و دسته‌بندیِ سیگنال‌هایی که از بقیه گرفتم.
//
// عمداً خالص و بدون هیچ ایمپورتی از react-native، تا با `npm test` تست شه —
// همون قاعده‌ای که برای `approxTime` و `authLink` هم داریم.
//
// چرا ترتیبِ زمانی کافی نبود: سیگنالِ بی‌جواب یه کارِ مونده‌ست و سیگنالی که
// گفتی «نه» یه بایگانی. با ترتیبِ زمانی، اون دوتا قاطی می‌شدن و کاری که
// منتظرِ توئه لای بایگانی گم می‌شد.

export type Bucket =
  | 'in'          // گفتم می‌رم
  | 'pending'     // هنوز جواب ندادم
  | 'maybe'       // شاید / مشروط
  | 'no'          // نه این‌دفعه
  | 'notMine'     // تیپِ من نیست
  | 'attended'    // انجام شد و رفتم
  | 'hidden';     // نباید دیده شه

/** فقط چیزهایی که برای تصمیمِ ترتیب لازمه — نه کلِ مدل. */
export interface Orderable {
  myResponse?: string | null;
  declineReason?: string | null;
  status?: string | null;
  startsAt?: string | null;
  /** پایانِ بازه‌ی چندروزه (مایگریشن 0057). */
  endsAt?: string | null;
  /** ساعت انتخاب نشده - فقط روز. نگاه کن به `Signal.dateOnly`. */
  dateOnly?: boolean | null;
  createdAt?: string | null;
  /** برنامه‌ی قطعی‌شده، اگر باشد — زمانِ نهایی‌اش بر زمانِ خودِ سیگنال مقدم است. */
  plan?: { finalStartsAt?: string | null } | null;
}

/**
 * لحظه‌ی برنامه: زمانِ نهاییِ قطعی‌شده، وگرنه زمانِ خودِ سیگنال.
 * همان قاعده‌ی `planWhen` در planBoard.ts، برای ورودیِ لاغرِ این فایل.
 */
export const whenOf = (s: Orderable): string | null =>
  s.plan?.finalStartsAt ?? s.startsAt ?? null;

/**
 * آیا زمانِ این سیگنال گذشته؟ سیگنالِ بی‌زمان هیچ‌وقت نمی‌گذره.
 *
 * وقتی ساعتی در کار نیست، «گذشته» یعنی **روز** تموم شده، نه لحظه‌ی ذخیره‌شده:
 * تاریخِ بی‌ساعت ظهر ذخیره می‌شه (نگاه کن به core/lib/dateChoice.ts) و بدونِ
 * این قاعده، دعوتِ امروز سرِ ساعتِ ۱۲:۰۱ از فهرست می‌افتاد بیرون. بازه هم
 * همین‌طور: تا آخرِ روزِ آخرش هنوز جریان داره.
 */
export function isPast(s: Orderable, now: Date = new Date()): boolean {
  if (s.status === 'completed' || s.status === 'cancelled') return true;
  // برنامه‌ی قطعی‌شده یک لحظه‌ی نهاییِ دقیق دارد که بر بازه/روزِ خودِ
  // سیگنال مقدم است (WHOSUP-165): قراری که برای ۲۰ام قطعی شده و ۲۰ام گذشته،
  // گذشته است — حتی اگر سیگنال اولش «۲۵ام» گفته بود.
  const final = s.plan?.finalStartsAt;
  const iso = final || s.endsAt || s.startsAt;
  if (!iso) return false;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return false;
  const end = (!final && (s.endsAt || s.dateOnly))
    ? new Date(at.getFullYear(), at.getMonth(), at.getDate() + 1).getTime()
    : at.getTime();
  return end < now.getTime();
}

/**
 * سیگنال به کدوم دسته می‌خوره.
 *
 * `hidden` یعنی اصلاً نشون داده نشه: چیزی که گذشته و توش نبودم، فقط
 * فهرست رو شلوغ می‌کنه.
 */
export function bucketOf(s: Orderable, now: Date = new Date()): Bucket {
  const answered = s.myResponse && s.myResponse !== 'pending';

  /**
   * لغوشده، هرکسی را که یک‌جور جوابی داده بود می‌مانَد — «هستم»، «شاید»،
   * «شرطی».
   *
   * قبلاً از `isPast` رد می‌شد که لغوشده را «گذشته» حساب می‌کند، و بعد
   * قاعده‌ی «گذشته‌ای که توش نبودم، هیچی» آن را `hidden` می‌کرد. نتیجه:
   * کسی که «شاید» گفته بود، اعلانِ «لغو شد» می‌گرفت و بعد می‌رفت توی اپ و
   * می‌دید برنامه **ناپدید** شده — نه لغو، ناپدید.
   *
   * این دقیقاً همان فرضِ «فقط `in` آدم است» است که مایگریشن 0120 در
   * دیتابیس برداشت؛ این نیمه‌ی رابطش. هر دو باید یک چیز بگویند، وگرنه
   * دیتابیس سیگنال را نگه می‌دارد و رابط پنهانش می‌کند.
   *
   * `signalRepo.incoming` هم عمداً `cancelled` را می‌آورد و توضیحش را
   * بالای همان کوئری نوشته — این‌جا دور ریخته می‌شد.
   */
  if (s.status === 'cancelled') {
    const engaged = s.myResponse === 'in' || s.myResponse === 'maybe'
      || s.myResponse === 'conditional';
    return engaged ? 'attended' : 'hidden';
  }

  if (isPast(s, now)) {
    // گذشته‌ای که توش بودم، تاریخچه‌ی منه. گذشته‌ای که نبودم، هیچی.
    return s.myResponse === 'in' ? 'attended' : 'hidden';
  }

  if (!answered) return 'pending';
  if (s.myResponse === 'in') return 'in';
  if (s.myResponse === 'maybe' || s.myResponse === 'conditional') return 'maybe';
  if (s.myResponse === 'out') {
    return s.declineReason === 'not_my_thing' ? 'notMine' : 'no';
  }
  return 'pending';
}

/** ترتیبِ دسته‌ها، از «کارِ مونده» تا «بایگانی». */
const RANK: Record<Bucket, number> = {
  in: 0, pending: 1, maybe: 2, no: 3, notMine: 4, attended: 5, hidden: 6,
};

/** دسته‌هایی که کمرنگ نشون داده می‌شن: دیگه کاری ازت نمی‌خوان. */
export const DIMMED: Bucket[] = ['no', 'notMine', 'attended'];

/**
 * مرتب می‌کنه و `hidden`ها رو می‌ندازه بیرون.
 * داخلِ هر دسته، تازه‌ترین بالاست.
 */
export function orderIncoming<T extends Orderable>(list: T[], now: Date = new Date()): T[] {
  return list
    .filter((s) => bucketOf(s, now) !== 'hidden')
    .sort((a, b) => {
      const d = RANK[bucketOf(a, now)] - RANK[bucketOf(b, now)];
      if (d !== 0) return d;
      const at = new Date(a.startsAt ?? a.createdAt ?? 0).getTime();
      const bt = new Date(b.startsAt ?? b.createdAt ?? 0).getTime();
      return bt - at;
    });
}

/* ــــ فیدِ یکپارچه ــــ */

/**
 * سیگنالِ خودم توی فیدِ مشترک کجا می‌شینه.
 *
 * هیچ‌وقت `hidden` نمی‌شه: سیگنالی که خودم ساختم، حتی اگه گذشته باشه،
 * تاریخچه‌ی منه — برخلافِ دعوتی که ردش کردم و گذشت.
 */
export function hostBucketOf(s: Orderable, now: Date = new Date()): Bucket {
  return isPast(s, now) ? 'attended' : 'in';
}

export type FeedEntry<I, M> =
  | { kind: 'incoming'; item: I }
  | { kind: 'mine'; item: M };

/**
 * یک فهرست از هر دو منبع.
 *
 * چرا اصلاً: دو سربرگِ جدا یعنی هر بار باید حدس بزنی کارِ مونده کدوم
 * طرفه. با یه فهرست، ترتیب خودش جواب می‌ده.
 *
 * ترتیب همون منطقِ `orderIncoming` است — پس «کارِ مونده» بالا می‌مونه و
 * سیگنالِ خودم قاطیِ همون نظم می‌شه، نه بالای همه‌چی.
 */
export function orderFeed<I extends Orderable, M extends Orderable>(
  incoming: I[], mine: M[], now: Date = new Date(),
): FeedEntry<I, M>[] {
  const rows: { rank: number; at: number; entry: FeedEntry<I, M> }[] = [];

  const timeOf = (s: Orderable) =>
    new Date(s.startsAt ?? s.createdAt ?? 0).getTime();

  for (const s of incoming) {
    const b = bucketOf(s, now);
    if (b === 'hidden') continue;
    rows.push({ rank: RANK[b], at: timeOf(s), entry: { kind: 'incoming', item: s } });
  }
  for (const s of mine) {
    rows.push({ rank: RANK[hostBucketOf(s, now)], at: timeOf(s), entry: { kind: 'mine', item: s } });
  }

  return rows
    .sort((a, b) => (a.rank - b.rank) || (b.at - a.at))
    .map((r) => r.entry);
}

/* ــــ صفحه‌ی «برنامه‌ها»: پیشِ رو / گذشته ــــ */

/** یک ردیفِ فید به‌علاوه‌ی لحظه‌اش — تا تقویم بتواند روی روزش بنشاندش. */
export type PlanRow<I, M> = FeedEntry<I, M> & { when: string | null };

/**
 * همان فید، ولی بخش‌بخش و به ترتیبِ زمان (WHOSUP-165) — به خواستِ کاربر،
 * «مثلِ صفحه‌ی تقویم»: پیشِ رو صعودی (نزدیک‌ترین اول)، گذشته نزولی
 * (تازه‌ترین اول — همان که آدم دنبالش می‌گردد).
 *
 * چهار بخش، به همین ترتیب (WHOSUP-235):
 *   upcoming   زمانش نیامده
 *   undated    اصلاً زمان ندارد («هنوز بی‌تاریخ») — قرینه‌ی همان بخش در
 *              صفحه‌ی برنامه‌های وب. قبلاً تهِ «پیشِ رو» می‌نشست و آن‌جا مثلِ
 *              دورترین برنامه خوانده می‌شد، نه مثلِ چیزی که هنوز تاریخ ندارد.
 *   past       زمانش رفته یا تمام شده
 *   cancelled  لغو شده — بخشِ خودش، **بعد از** گذشته‌ها
 *
 * چرا لغوشده از «گذشته» جدا شد: `isPast` لغوشده را گذشته حساب می‌کند (و
 * درست هم هست، کاری ازش نمی‌آید)، ولی «هفته‌ی پیش رفتیم» و «لغو شد» دو خبرِ
 * متفاوتند و قاطیِ هم خواندنشان سخت بود. ترتیبِ بخش‌ها خودش حرف می‌زند:
 * لغوشده تهِ همه، چون کم‌ارزش‌ترین چیز برای مرور کردن است.
 *
 * عضویت عیناً همان فید است، عمداً: لغوشده‌ای که رویش پایه بودم و دعوتی که
 * ردش کردم می‌مانند (کم‌رنگ) — همان تصمیمِ ثبت‌شده‌ی `bucketOf` و
 * مایگریشن 0120.
 */
export function splitFeed<I extends Orderable, M extends Orderable>(
  incoming: I[], mine: M[], now: Date = new Date(),
): {
  upcoming: PlanRow<I, M>[]; undated: PlanRow<I, M>[];
  past: PlanRow<I, M>[]; cancelled: PlanRow<I, M>[];
} {
  const rows: PlanRow<I, M>[] = [];
  for (const s of incoming) {
    if (bucketOf(s, now) === 'hidden') continue;
    rows.push({ kind: 'incoming', item: s, when: whenOf(s) });
  }
  for (const s of mine) rows.push({ kind: 'mine', item: s, when: whenOf(s) });

  const ms = (iso: string | null) => (iso ? new Date(iso).getTime() : NaN);
  const upcoming: PlanRow<I, M>[] = [];
  const undated: PlanRow<I, M>[] = [];
  const past: PlanRow<I, M>[] = [];
  const cancelled: PlanRow<I, M>[] = [];
  for (const r of rows) {
    // لغو شدن بر هر چیزِ دیگری مقدم است: برنامه‌ی لغوشده‌ی فردا نه «پیشِ رو»
    // است نه «بی‌تاریخ»، لغو شده است.
    if (r.item.status === 'cancelled') cancelled.push(r);
    else if (!r.when) undated.push(r);
    else if (isPast(r.item, now)) past.push(r);
    else upcoming.push(r);
  }

  // تاریخِ خراب (NaN) ته می‌ماند به‌جای اینکه ترتیب را بهم بریزد.
  upcoming.sort((a, b) => {
    const at = ms(a.when);
    const bt = ms(b.when);
    if (Number.isNaN(at)) return Number.isNaN(bt) ? 0 : 1;
    if (Number.isNaN(bt)) return -1;
    return at - bt;
  });
  past.sort((a, b) => (ms(b.when) || 0) - (ms(a.when) || 0));
  // لغوشده‌ی بی‌تاریخ هم ممکن است باشد؛ `|| 0` تهِ فهرست می‌نشاندش.
  cancelled.sort((a, b) => (ms(b.when) || 0) - (ms(a.when) || 0));
  return { upcoming, undated, past, cancelled };
}
